import React, { useEffect, useState, useRef } from "react";
import JoditEditor from "jodit-react";
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

// Helper function to format image URLs
const formatImageUrl = (url) => {
  if (!url) return "";
  return url.replace(/\\/g, "/");
};

// Helper function to format time (e.g., "14:00" -> "2:00 PM")
const formatTime = (timeString) => {
  if (!timeString) return "N/A";
  try {
    const [hours, minutes] = timeString.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedHours = h % 12 || 12;
    return `${formattedHours}:${minutes} ${ampm}`;
  } catch (e) {
    return timeString;
  }
};

function AddProduct() {
  const editor = useRef(null);
  const productImageRef = useRef(null);
  const galleryImagesRef = useRef(null);
  const token = sessionStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [formdata, setformdata] = useState({
    product_name: "",
    category: "",
    sub_category: "",
    sku: "",
    variation: "Simple",
    description: "",
    reguler_price: "",
    discount_price: "",
    stock: "",
    attributes: [],
    galleryImages: [],
    productImage: null,
    attributeValues: {},
    combinedValue: [],
    priceData: {},
    discountPriceData: {},
    stockData: {},
    displayShifts: [],
  });
  const [shiftList, setShiftList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [attributeName, setAttributeName] = useState("");
  const [tempValues, setTempValues] = useState({});
  const [subCategoryList, setSubCategoryList] = useState([]);

  // Add preview state for uploaded images
  const [imagePreview, setImagePreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  // Fetch category list
  useEffect(() => {
    const fetchCategoryList = async () => {
      if (!token) {
        toast.error("No token provided!");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APIURL}api/catagorylist?isAdmin=true`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCategoryList(response.data?.catagories || []);
      } catch (error) {
        toast.error(
          "Error fetching data: " +
          (error.response?.data?.message || "Something went wrong!")
        );
      }
    };

    fetchCategoryList();

    const fetchShiftList = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_APIURL}api/shift/all`);
        if (response.data.status) {
          setShiftList(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching shifts:", error);
      }
    };
    fetchShiftList();
  }, [token]);

  // Update handleVariationChange
  const handleVariationChange = (e) => {
    setformdata({ ...formdata, variation: e.target.value });
  };

  // Update handleAttributeNameChange
  const handleAttributeNameChange = (e) => {
    setAttributeName(e.target.value);
  };

  // Update handleAddAttribute
  const handleAddAttribute = () => {
    if (attributeName.trim() !== "") {
      setformdata({
        ...formdata,
        attributes: [...formdata.attributes, attributeName],
      });
      setAttributeName("");
    }
  };

  // Update handleAttributeValueChange
  const handleAttributeValueChange = (attribute, e) => {
    setTempValues({
      ...tempValues,
      [attribute]: e.target.value,
    });
  };

  // Update handleAddValue
  const handleAddValue = (attribute) => {
    const value = tempValues[attribute];
    if (value && value.trim() !== "") {
      setformdata((prev) => {
        const updatedValues = {
          ...prev,
          attributeValues: {
            ...prev.attributeValues,
            [attribute]: [...(prev.attributeValues[attribute] || []), value],
          },
        };
        setTempValues({ ...tempValues, [attribute]: "" });
        return updatedValues;
      });
    }
  };


  useEffect(() => {
    if (formdata.variation === "Variable") {
      const generateCombinedValue = () => {
        const attributesArray = Object.values(formdata.attributeValues);
        if (attributesArray.length === 0) return [];

        const combine = (arr) =>
          arr.reduce(
            (acc, curr) => acc.flatMap((c) => curr.map((v) => [...c, v])),
            [[]]
          );

        return combine(attributesArray).map((combo) => combo.join(" - "));
      };

      const newCombinedValues = generateCombinedValue();
      setformdata((prev) => ({
        ...prev,
        combinedValue: newCombinedValues,
      }));
    }
  }, [formdata.attributeValues, formdata.variation]);

  // Update price handlers
  const handlePriceChange = (combo, e) => {
    setformdata((prev) => ({
      ...prev,
      priceData: {
        ...prev.priceData,
        [combo]: e.target.value,
      },
    }));
  };

  const handleDiscountPriceChange = (combo, e) => {
    setformdata((prev) => ({
      ...prev,
      discountPriceData: {
        ...prev.discountPriceData,
        [combo]: e.target.value,
      },
    }));
  };

  const handleStockChange = (combo, e) => {
    setformdata((prev) => ({
      ...prev,
      stockData: {
        ...prev.stockData,
        [combo]: e.target.value,
      },
    }));
  };

  // Update the product image handler
  const handleProductImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setformdata({
        ...formdata,
        productImage: file,
      });
      // Create preview URL
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Update gallery images handler
  const handleGalleryImagesChange = (event) => {
    const files = Array.from(event.target.files);
    setformdata((prev) => ({
      ...prev,
      galleryImages: files,
    }));
    // Create preview URLs
    const previewUrls = files.map((file) => URL.createObjectURL(file));
    setGalleryPreviews(previewUrls);
  };

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      galleryPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreview, galleryPreviews]);

  // Update form validation
  const validateForm = () => {
    if (!formdata.product_name.trim()) {
      toast.error("Product Name is required.");
      return false;
    }
    if (!formdata.category) {
      toast.error("Category is required.");
      return false;
    }
    if (!formdata.sub_category) {
      toast.error("Sub Category is required.");
      return false;
    }
    if (!formdata.sku.trim()) {
      toast.error("SKU is required.");
      return false;
    }
    if (formdata.variation === "Simple") {
      if (
        !formdata.reguler_price ||
        !formdata.discount_price ||
        !formdata.stock
      ) {
        toast.error("Regular Price, Discount Price and Stock are required.");
        return false;
      }
    } else {
      if (Object.keys(formdata.attributeValues).length > 0) {
        if (formdata.combinedValue.length === 0) {
          toast.error("Please select at least one attribute combination.");
          return false;
        }
        const isValid = formdata.combinedValue.every(
          (combo) =>
            formdata.priceData[combo] &&
            formdata.discountPriceData[combo] &&
            formdata.stockData[combo]
        );
        if (!isValid) {
          toast.error(
            "Please fill in regular price, discount price, and stock for all variations."
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleChangeCategory = async (e) => {
    try {
      setformdata({ ...formdata, category: e.target.value });
      const response = await axios.get(
        `${process.env.REACT_APP_APIURL}api/sub-category/list?category_id=${e.target.value}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      let subCategoryArr = [];
      if (response?.data?.status === 1) {
        subCategoryArr = response?.data?.data;
      }
      setSubCategoryList(subCategoryArr || []);
    } catch (error) {
      toast.error(
        "Error fetching data: " +
        (error.response?.data?.message || error.message)
      );
    }
  };

  // Update handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      const submitFormData = new FormData();

      // Append basic product fields
      submitFormData.append("product_name", formdata.product_name);
      submitFormData.append("category", formdata.category);
      submitFormData.append("sub_category", formdata.sub_category);
      submitFormData.append("sku", formdata.sku);
      submitFormData.append("variation", formdata.variation);
      submitFormData.append("description", formdata.description);
      submitFormData.append("displayShifts", JSON.stringify(formdata.displayShifts));

      if (formdata.variation === "Simple") {
        submitFormData.append("reguler_price", Number(formdata.reguler_price));
        submitFormData.append(
          "discount_price",
          Number(formdata.discount_price)
        );
        submitFormData.append("stock", Number(formdata.stock));
      }

      if (
        formdata.variation === "Variable" &&
        Object.keys(formdata.attributeValues).length > 0
      ) {
        // Format the attributes array according to the required pattern
        const formattedAttributes = formdata.combinedValue.map((combo) => {
          // Split the combination string to get individual attribute values
          const values = combo.split(" - ");

          // Create an object with attribute names and values
          const attributeObj = {};
          formdata.attributes.forEach((attr, index) => {
            attributeObj[attr.toLowerCase()] = values[index];
          });

          // Add other_attributes with price, discount_price, and stock
          return {
            ...attributeObj,
            other_attributes: {
              price: Number(formdata.priceData[combo] || 0),
              discount_price: Number(formdata.discountPriceData[combo] || 0),
              stock: Number(formdata.stockData[combo] || 0),
            },
          };
        });

        // Add base prices for variable product (using first variant's price)
        submitFormData.append(
          "reguler_price",
          Number(formdata.priceData[formdata.combinedValue[0]] || 0)
        );
        submitFormData.append(
          "discount_price",
          Number(formdata.discountPriceData[formdata.combinedValue[0]] || 0)
        );

        // Append the formatted attributes array
        submitFormData.append(
          "attributes",
          JSON.stringify(formattedAttributes)
        );
      }

      // Append product image and gallery images
      if (formdata.productImage) {
        const file = formdata.productImage;
        // Create a new file name with forward slashes
        const fileName = formatImageUrl(file.name);
        const modifiedFile = new File([file], fileName, {
          type: file.type,
        });
        submitFormData.append("productImage", modifiedFile);
      }

      formdata.galleryImages.forEach((file) => {
        // Create a new file name with forward slashes
        const fileName = formatImageUrl(file.name);
        const modifiedFile = new File([file], fileName, {
          type: file.type,
        });
        submitFormData.append("galleryImages", modifiedFile);
      });

      try {
        const response = await axios.post(
          formatImageUrl(`${process.env.REACT_APP_APIURL}api/addproduct`),
          submitFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Check for success in the response data
        if (response.data.success) {
          toast.success("Product added successfully!");
          // Reset form data
          setformdata({
            product_name: "",
            category: "",
            sub_category: "",
            sku: "",
            variation: "Simple",
            description: "",
            reguler_price: "",
            discount_price: "",
            stock: "",
            attributes: [],
            galleryImages: [],
            productImage: null,
            attributeValues: {},
            combinedValue: [],
            priceData: {},
            discountPriceData: {},
            stockData: {},
            displayShifts: [],
          });

          // Reset previews
          setImagePreview(null);
          setGalleryPreviews([]);

          // Reset file inputs
          if (productImageRef.current) {
            productImageRef.current.value = "";
          }
          if (galleryImagesRef.current) {
            galleryImagesRef.current.value = "";
          }
        } else {
          toast.error(response.data.message || "Failed to add product!");
        }
      } catch (error) {
        console.error("Error adding product:", error);
        toast.error(error.response?.data?.message || "Something went wrong!");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Products</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item active">Add Product</li>
            </ol>
          </nav>
        </div>
        <div className="card">
          <div className="card-body">
            <form className="row g-3 pt-4" onSubmit={handleSubmit}>
              {/* Product Name */}
              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    placeholder="Enter Product Name"
                    value={formdata.product_name}
                    onChange={(e) =>
                      setformdata({ ...formdata, product_name: e.target.value })
                    }
                  />
                  <label htmlFor="name">Product Name</label>
                </div>
              </div>

              {/* Category */}
              <div className="col-md-4">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="category"
                    value={formdata.category}
                    onChange={handleChangeCategory}
                  >
                    <option value="">Select Category</option>
                    {categoryList.map((item, index) => (
                      <option key={index} value={item._id}>
                        {item.catagoryname}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="category">Category</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="sub_category"
                    value={formdata.sub_category}
                    onChange={(e) =>
                      setformdata({ ...formdata, sub_category: e.target.value })
                    }
                  >
                    <option value="">Select Sub Category</option>
                    {subCategoryList.map((item, index) => (
                      <option key={index} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="sub_category">Sub Category</label>
                </div>
              </div>
              {/* SKU */}
              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="sku"
                    placeholder="Enter SKU"
                    value={formdata.sku}
                    onChange={(e) =>
                      setformdata({ ...formdata, sku: e.target.value })
                    }
                  />
                  <label htmlFor="sku">SKU</label>
                </div>
              </div>

              {/* Variation */}
              <div className="col-md-3">
                <div className="form-floating mb-3">
                  <select
                    className="form-select"
                    id="variation"
                    value={formdata.variation}
                    onChange={handleVariationChange}
                  >
                    <option value="Simple">Simple</option>
                    <option value="Variable">Variable</option>
                  </select>
                  <label htmlFor="variation">Product Variation</label>
                </div>
              </div>

              {/* Simple Product Prices */}
              {formdata.variation === "Simple" && (
                <>
                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="reguler_price"
                        placeholder="Enter Regular Price"
                        value={formdata.reguler_price}
                        onChange={(e) =>
                          setformdata({
                            ...formdata,
                            reguler_price: e.target.value,
                          })
                        }
                      />
                      <label htmlFor="reguler_price">Regular Price</label>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="discount_price"
                        placeholder="Enter Discount Price"
                        value={formdata.discount_price}
                        onChange={(e) =>
                          setformdata({
                            ...formdata,
                            discount_price: e.target.value,
                          })
                        }
                      />
                      <label htmlFor="discount_price">Discount Price</label>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="stock"
                        placeholder="Enter Stock"
                        value={formdata.stock}
                        onChange={(e) =>
                          setformdata({ ...formdata, stock: e.target.value })
                        }
                      />
                      <label htmlFor="stock">Stock</label>
                    </div>
                  </div>
                </>
              )}

              {/* Variable Product Attributes */}
              {formdata.variation === "Variable" && (
                <>
                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="attribute_name"
                        placeholder="Enter Attribute Name"
                        value={attributeName}
                        onChange={handleAttributeNameChange}
                      />
                      <label htmlFor="attribute_name">Attribute Name</label>
                    </div>
                  </div>

                  <div className="col-md-2 d-flex align-items-center mt-0">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddAttribute}
                      style={{ backgroundColor: "#7657f1", border: "none" }}
                    >
                      Add Attribute
                    </button>
                  </div>

                  {formdata.attributes.map((attribute, index) => (
                    <>
                      <div key={index} className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id={`attribute_${attribute}`}
                            placeholder={`Enter ${attribute}`}
                            value={tempValues[attribute] || ""}
                            onChange={(e) =>
                              handleAttributeValueChange(attribute, e)
                            }
                          />
                          <label htmlFor={`attribute_${attribute}`}>
                            {attribute}
                          </label>
                        </div>
                      </div>
                      <div className="col-md-2 d-flex align-items-center mt-3">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleAddValue(attribute)}
                          style={{ backgroundColor: "#7657f1", border: "none" }}
                        >
                          Add Value
                        </button>
                      </div>
                    </>
                  ))}

                  {formdata.combinedValue.length > 0 && (
                    <div className="col-12">
                      <h3 className="my-3 mb-4">Attributes</h3>
                      {formdata.combinedValue.map((combo, idx) => (
                        <div key={idx} className="row mb-3">
                          <div className="col-md-2 d-flex align-items-center">
                            <button type="button" className="btn btn-danger">
                              {combo}
                            </button>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Regular Price"
                                id={combo}
                                value={formdata.priceData[combo] || ""}
                                onChange={(e) => handlePriceChange(combo, e)}
                              />
                              <label htmlFor={combo}>Reguler Price</label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Discount Price"
                                id={combo}
                                value={formdata.discountPriceData[combo] || ""}
                                onChange={(e) =>
                                  handleDiscountPriceChange(combo, e)
                                }
                              />
                              <label htmlFor={combo}>Discount Price</label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Stock"
                                id={combo}
                                value={formdata.stockData[combo] || ""}
                                onChange={(e) => handleStockChange(combo, e)}
                              />
                              <label htmlFor={combo}>Stock</label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Product Image */}
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="file"
                    className="form-control"
                    id="productImage"
                    ref={productImageRef}
                    onChange={handleProductImageChange}
                    accept="image/*"
                  />
                  <label htmlFor="productImage">Product Image</label>
                </div>
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Product preview"
                      className="img-thumbnail"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                )}
              </div>

              {/* Gallery Images */}
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    ref={galleryImagesRef}
                    onChange={handleGalleryImagesChange}
                    accept="image/*"
                  />
                  <label htmlFor="galleryImages">Product Gallery</label>
                </div>
              </div>

              {/* Gallery Images Preview */}
              {galleryPreviews.length > 0 && (
                <div className="col-12">
                  <div className="preview-images d-flex flex-wrap gap-2">
                    {galleryPreviews.map((previewUrl, index) => (
                      <img
                        key={index}
                        src={previewUrl}
                        alt={`Gallery preview ${index + 1}`}
                        className="img-thumbnail"
                        style={{ height: "100px", objectFit: "cover" }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="col-md-12">
                <div className="form-control mb-3">
                  <label htmlFor="description">Description</label>
                  <JoditEditor
                    ref={editor}
                    value={formdata.description}
                    onChange={(newContent) =>
                      setformdata({ ...formdata, description: newContent })
                    }
                  />
                </div>
              </div>

              {/* Product Display Shift (Multi-select) */}
              <div className="col-12">
                <div className="p-3 border rounded">
                  <label className="form-label d-block mb-3 fw-bold">Product Display Shift</label>
                  <div className="d-flex flex-wrap gap-3">
                    {shiftList.map((shift) => (
                      <div key={shift._id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`shift_${shift._id}`}
                          value={shift._id}
                          checked={formdata.displayShifts?.includes(shift._id) || false}
                          onChange={(e) => {
                            const val = e.target.value;
                            const isChecked = e.target.checked;
                            setformdata(prev => {
                              const currentShifts = prev.displayShifts || [];
                              return {
                                ...prev,
                                displayShifts: isChecked
                                  ? [...currentShifts, val]
                                  : currentShifts.filter(id => id !== val)
                              };
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor={`shift_${shift._id}`}>
                          {shift.name} <br />
                          <small className="text-muted">{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</small>
                        </label>
                      </div>
                    ))}
                    {shiftList.length === 0 && <span className="text-muted">No shifts found</span>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-start d-flex gap-3 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </>
  );
}

export default AddProduct;
